import { useCallback, useEffect, useRef, useState } from "react";
import type { ProductChange, ProductRevision, ShopOption, ShopVariant } from "@stackje/site-builder";
import type { ShopApi, ShopIndex, ShopProductEntry } from "../../api/contract";
import { ShopRefusedError } from "../../api/errors";
import { productVariants, type ProductSection } from "../../lib/shop";
import { moneyInput, parseMoney } from "../../lib/shopMoney";
import { shopRequest, ShopTimeoutError } from "./shopRequest";
import { useShopLoad } from "./useShopLoad";

/** A variant as it is edited: its prices are text until they are saved. */
export type EditableVariant = ShopVariant & { priceText: string; compareText: string };

export const editableVariants = (product: ShopProductEntry): EditableVariant[] => productVariants(product).map((variant) => ({
  ...variant,
  priceText: moneyInput(variant.price, variant.currency),
  compareText: variant.compareAt === null ? "" : moneyInput(variant.compareAt, variant.currency),
}));

export type ProductConfirm = "available" | "hidden" | "delete";

export interface ProductEditorOptions {
  api: ShopApi;
  siteId: string;
  shop: ShopIndex;
  product: ShopProductEntry;
  language: string;
  reload: () => Promise<void>;
  onBack: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * Everything the product editor's tabs share: what is waiting for Save (prices, options, files), what saves as you go,
 * and the one way every save runs (busy, refused, reload, Undo).
 */
export function useProductEditor({ api, siteId, shop, product, language, reload, onBack, onDirtyChange }: ProductEditorOptions) {
  const [section, setSection] = useState<ProductSection>("details");
  const [busy, setBusy] = useState(false);
  const working = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [saved, setSaved] = useState(false);
  const [confirm, setConfirm] = useState<ProductConfirm | null>(null);
  const [customDirty, setCustomDirty] = useState(false);
  const [revision, setRevision] = useState<ProductRevision | null>(null);
  const [defaultPrice, setDefaultPrice] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditableVariant[] | null>(null);
  const [options, setOptions] = useState<ShopOption[] | null>(null);
  const [files, setFiles] = useState<typeof product.details.files | null>(null);
  const [history, setHistory] = useState<Awaited<ReturnType<ShopApi["getProductHistory"]>> | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const loadRevision = useCallback((signal: AbortSignal) => api.getProductRevision(siteId, product.details.revision, signal), [api, siteId, product.details.revision]);
  const revisionLoad = useShopLoad(loadRevision, `${siteId}/${product.details.revision}`);
  const loadHistory = useCallback((signal: AbortSignal) => api.getProductHistory(siteId, product.id, signal), [api, siteId, product.id]);
  const historyLoad = useShopLoad(loadHistory, historyOpen ? `${siteId}/${product.id}` : null);
  const currency = shop.settings!.currency;
  const savedVariants = editableVariants(product);
  const variants = draft ?? savedVariants;
  const dirty = Boolean(defaultPrice !== null || draft || options || files || customDirty);

  useEffect(() => { setRevision(revisionLoad.data); }, [revisionLoad.data]);
  useEffect(() => { setHistory(historyLoad.data); }, [historyLoad.data]);
  useEffect(() => { onDirtyChange?.(dirty); return () => onDirtyChange?.(false); }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (!dirty) return;
    const prevent = (event: BeforeUnloadEvent) => { event.preventDefault(); };
    window.addEventListener("beforeunload", prevent);
    return () => window.removeEventListener("beforeunload", prevent);
  }, [dirty]);

  /** Runs a save: busy while it goes, the screen reloaded after, and "Saved · live now" with Undo when `undoable`. */
  const work = async (action: () => Promise<unknown>, undoable = true, longUpload = false): Promise<boolean> => {
    if (working.current) return false; working.current = true;
    setBusy(true); setError(null);
    try { if (longUpload) await action(); else await shopRequest(() => action()); await shopRequest(() => reload()); setSaved(true); setCanUndo(undoable); return true; }
    catch (caught) { if (caught instanceof DOMException && caught.name === "AbortError") return false; setError(caught instanceof ShopRefusedError || caught instanceof ShopTimeoutError ? caught.message : "This didn't save. Reload and try again; your input is kept."); return false; }
    finally { working.current = false; setBusy(false); }
  };
  const saveDetails = (set: ProductChange) => work(() => api.saveProduct(siteId, product.id, product.rowVersion, set));
  const updateVariant = (index: number, change: Partial<EditableVariant>) => setDraft(variants.map((item, at) => at === index ? { ...item, ...change } : item));
  /** The prices of this variant that differ from what is saved. */
  const priceChanged = (variant: EditableVariant) => {
    const was = savedVariants.find((item) => item.id === variant.id);
    return Boolean(draft) && (!was || was.priceText !== variant.priceText);
  };
  const compareChanged = (variant: EditableVariant) => {
    const was = savedVariants.find((item) => item.id === variant.id);
    return Boolean(draft) && (!was || was.compareText !== variant.compareText);
  };

  const savePrices = async () => {
    const next: ShopVariant[] = [];
    for (const { priceText, compareText, ...variant } of variants) {
      const price = parseMoney(priceText, currency, language);
      const compareAt = compareText.trim() ? parseMoney(compareText, currency, language) : null;
      if ((priceText.trim() && price === null) || (compareText.trim() && compareAt === null)) { setError("Enter prices in the shop currency, with its correct number of decimal places."); return; }
      next.push({ ...variant, price, compareAt });
    }
    if (await work(() => api.saveProductVariants(siteId, product.id, product.rowVersion, next))) setDraft(null);
  };
  /** The Save button: whichever of options, files or prices is waiting. */
  const save = async () => {
    if (defaultPrice !== null) {
      const price = parseMoney(defaultPrice, currency, language);
      if (defaultPrice.trim() && price === null) { setError("Check the price for new variants."); return; }
      if (await saveDetails({ price })) setDefaultPrice(null);
    }
    else if (options) { if (await work(() => api.saveProductOptions(siteId, product.id, product.rowVersion, options))) setOptions(null); }
    else if (files) { if (await saveDetails({ files })) setFiles(null); }
    else await savePrices();
  };
  const discard = () => { setDefaultPrice(null); setDraft(null); setOptions(null); setFiles(null); setError(null); };
  const changeSection = (next: ProductSection) => {
    if (dirty) { setError("Save or discard your changes before switching sections."); return; }
    setSection(next); setError(null);
  };
  const openHistory = () => { setHistoryOpen(true); historyLoad.retry(); };
  const closeHistory = () => { setHistoryOpen(false); setHistory(null); };
  /** The confirmed availability change or delete. */
  const confirmAction = (action: ProductConfirm) => work(async () => {
    if (action === "delete") { await api.deleteProduct(siteId, product.id, product.rowVersion); onBack(); }
    else await api.setProductAvailability(siteId, product.id, product.rowVersion, action);
    setConfirm(null);
  }, false);

  /** What the save bar says is waiting. */
  const waiting = (() => {
    if (defaultPrice !== null) return "Price for new variants changed · not saved yet";
    if (options) return "Options changed · not saved yet · saving rebuilds the variants";
    if (files) return "Files changed · not saved yet";
    const prices = variants.filter((variant) => priceChanged(variant) || compareChanged(variant)).length;
    if (prices) return `${prices === 1 ? "Price" : `${prices} prices`} changed · not saved yet · buyers see ${prices === 1 ? "it" : "them"} when you save`;
    return "Changes · not saved yet";
  })();

  return {
    api, siteId, shop, product, language, currency, reload, onBack,
    section, changeSection, busy, error, setError, canUndo, saved, confirm, setConfirm, dirty, waiting,
    revision, setRevision, defaultPrice, setDefaultPrice, draft, variants, updateVariant, priceChanged, compareChanged, setDraft,
    options, setOptions, files, setFiles, history, setHistory, historyOpen, historyLoad, revisionLoad, setCustomDirty,
    work, saveDetails, save, discard, openHistory, closeHistory, confirmAction,
  };
}

export type ProductEditorState = ReturnType<typeof useProductEditor>;
